import type { FirstHomeDutyOutput, TransferDutyOutput } from "./types.js";

// Plain-English result copy for the transfer (stamp) duty calculator. Lives in
// the rules package so every surface (Expo result screen, "my results" list,
// PDF report) renders identical wording from one source — drift between
// channels is the information → advice kill-threat (validation §3).
//
// Regulatory guardrails baked into the wording:
//  - Information, not advice. We state what the duty IS and how it is derived
//    from the state revenue office's bracket table; no "you should", no product
//    recommendation, no "switch".
//  - The first-home concession is presented as the DIFFERENCE between two cited
//    duty figures (full duty vs concessional duty) — never a "you save $X"
//    claim. The wording deliberately frames it as "the difference between …".
//  - Every figure cites its state revenue office + a verified-at date, with an
//    "as at <date>, verify with <revenue office>" caveat (ACL stale-figure
//    mitigation, panels §2 condition 2).

const STATE_OFFICE: Record<string, string> = {
  NSW: "Revenue NSW",
  VIC: "the State Revenue Office Victoria",
  QLD: "Queensland Revenue Office",
};

/**
 * The mandatory disclaimer rendered on every stamp-duty surface (CLAUDE.md
 * regulatory guardrails / validation §3 — binding, kill-threat if absent). Stamp
 * duty is a state tax, but the surface is the archetypal mortgage-broker lead
 * hook, so the information→advice line and the no-product-recommendation posture
 * must be stated explicitly, matching the sibling `first-home-scheme` module.
 */
export const STAMP_DUTY_DISCLAIMER =
  "Educational tool. Not financial advice. This is general information about " +
  "state transfer (stamp) duty, not a recommendation about any home loan, lender, " +
  "broker or product. Duty rates, thresholds and concessions are set by state " +
  "budgets and change — verify with your state revenue office before you act.";

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Duty rate as a per-$100 figure, the form the revenue offices publish. */
function per100(rate: number): string {
  return `$${(rate * 100).toFixed(2)} per $100`;
}

export function transferDutyHeadline(out: TransferDutyOutput): string {
  return `Transfer (stamp) duty on a ${formatAud(out.dutiableValue)} purchase in ${out.state}: ${formatAud(out.totalDuty)}`;
}

/** Per-bracket breakdown — the transparency the gov black-box tools hide. */
export function transferDutyBreakdown(out: TransferDutyOutput): string[] {
  const lines = [
    `General transfer duty: ${formatAud(out.generalDuty)} (your ${formatAud(out.dutiableValue)} falls in the ${out.bracket.label} bracket, charged at ${per100(out.bracket.rate)}${out.bracket.over > 0 ? ` of the value over ${formatAud(out.bracket.over)}, plus a ${formatAud(out.bracket.base)} base` : ""}).`,
  ];
  if (out.foreignSurcharge > 0) {
    lines.push(
      `Foreign-purchaser surcharge duty: ${formatAud(out.foreignSurcharge)} (applied on top of general duty for foreign buyers of residential land).`,
    );
  }
  return lines;
}

/**
 * First-home concession value, framed as the DIFFERENCE between two cited duty
 * figures. Never says "you save". The new-build branch is called out explicitly.
 */
export function firstHomeDutyBullet(out: FirstHomeDutyOutput): string {
  if (out.branch === "qld-new-build") {
    return `For a new home in ${out.state}, first-home buyers pay nil transfer duty with no value cap (contracts on/after 1 May 2025). The general duty on this ${formatAud(out.dutiableValue)} purchase would otherwise be ${formatAud(out.fullDuty)}; with the new-home first-home concession the duty payable is ${formatAud(0)} — a difference of ${formatAud(out.concessionValue)}.`;
  }
  if (!out.concessionApplies) {
    return `At ${formatAud(out.dutiableValue)}, this purchase is above the ${out.state} first-home concession threshold, so the full general transfer duty of ${formatAud(out.fullDuty)} applies.`;
  }
  if (out.branch === "full-exemption") {
    return `As a first-home purchase at ${formatAud(out.dutiableValue)}, this is at or below the ${out.state} first-home exemption threshold, so the duty payable is ${formatAud(out.concessionDuty)}. The general (non-concessional) duty would be ${formatAud(out.fullDuty)} — a difference of ${formatAud(out.concessionValue)}.`;
  }
  return `As a first-home purchase at ${formatAud(out.dutiableValue)}, the ${out.state} concession reduces the transfer duty from ${formatAud(out.fullDuty)} (general duty) to ${formatAud(out.concessionDuty)} (concessional duty) — a difference of ${formatAud(out.concessionValue)}.`;
}

/** Source-attribution line, naming the state revenue office in force. */
export function sourceBullet(
  state: string,
  period: string,
  verifiedAt: string,
): string {
  const office = STATE_OFFICE[state] ?? `the ${state} revenue office`;
  return `These figures use the ${office} transfer-duty schedule for ${period}, last verified ${verifiedAt}. Duty rates, thresholds and surcharges are set by state budgets and can change — as at ${verifiedAt}, always verify with ${office}.`;
}

export function transferDutyExplanation(
  out: TransferDutyOutput,
  period: string,
  verifiedAt: string,
): string[] {
  return [
    ...transferDutyBreakdown(out),
    sourceBullet(out.state, period, verifiedAt),
    STAMP_DUTY_DISCLAIMER,
  ];
}
